import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { BrandMark } from './BrandMark';

const SIZES = {
  sm: 'size-8',
  md: 'size-14',
  lg: 'size-20',
} as const;

/**
 * The loading indicator, everywhere.
 *
 * The brand mark is a cube, so the loader hovers it: it rises and falls over its own
 * shadow while an arc sweeps around it. That replaces the anonymous grey ring the console
 * and the router were each drawing separately — three seconds of a spinner is the most
 * reliably-seen moment on a slow connection, and it was the one moment the product said
 * nothing about itself.
 *
 * `role="status"` with `aria-busy`, so a screen reader announces the wait rather than
 * meeting silence. The mark itself is decorative — `BrandMark` is already `aria-hidden` —
 * so `label` is the only thing announced, which is why it is a sentence and not "Loading".
 */
export function BrandLoader({
  label = 'Loading…',
  size = 'md',
  className,
  /** Hide the caption when the surrounding screen already says what is loading. */
  showLabel = true,
}: {
  label?: string;
  size?: keyof typeof SIZES;
  className?: string;
  showLabel?: boolean;
}): ReactNode {
  return (
    <div
      role="status"
      aria-busy="true"
      className={cn('flex flex-col items-center gap-5', className)}
    >
      <div className={cn('brand-loader', SIZES[size])}>
        <span aria-hidden="true" className="brand-loader__ring" />
        <span aria-hidden="true" className="brand-loader__shadow" />
        <BrandMark className={cn('brand-loader__mark', SIZES[size])} />
      </div>

      {/*
        The caption is `sr-only` rather than removed when hidden: `role="status"` with no
        text content announces nothing at all, so a "quiet" loader would be invisible to a
        screen reader instead of merely unobtrusive.
      */}
      <p className={cn('text-sm text-muted', !showLabel && 'sr-only')}>{label}</p>
    </div>
  );
}

/** How long the opening splash holds before it starts fading. */
const SPLASH_MS = 2000;

/**
 * The opening splash.
 *
 * Shown once per full page load, over whatever has already rendered underneath — it is an
 * overlay, not a gate, so the app boots normally behind it and nothing is blocked on the
 * timer. It leaves the DOM entirely once the fade finishes rather than lingering as an
 * invisible layer over the page.
 *
 * Worth knowing before changing the duration: two seconds is a deliberate brand beat, not
 * a measurement of anything. On a fast connection the app is ready long before it ends,
 * so every millisecond here is time a visitor waits on purpose. If that ever looks like
 * the wrong trade for the marketing pages — it is a real cost to how quick the site feels
 * — gating it on `sessionStorage` so it plays on arrival and not on every refresh is a
 * two-line change.
 */
export function SplashScreen(): ReactNode {
  const [phase, setPhase] = useState<'holding' | 'leaving' | 'done'>('holding');

  useEffect(() => {
    const hold = setTimeout(() => setPhase('leaving'), SPLASH_MS);
    // Matches the 400ms transition in `.splash-leaving`; unmounting earlier would cut
    // the fade, later would leave a dead layer over the page.
    const leave = setTimeout(() => setPhase('done'), SPLASH_MS + 400);

    return () => {
      clearTimeout(hold);
      clearTimeout(leave);
    };
  }, []);

  if (phase === 'done') return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex flex-col items-center justify-center gap-8 bg-surface',
        phase === 'leaving' && 'splash-leaving',
      )}
    >
      {/* The same aurora and lattice the homepage hero uses, so the first thing a visitor
          sees is already the site's own light rather than a blank white field. */}
      <div aria-hidden="true" className="aurora" />
      <div aria-hidden="true" className="dot-grid" />

      <div className="relative flex flex-col items-center gap-7">
        <BrandLoader size="lg" label="Loading The BlackBox Talent…" showLabel={false} />

        <p className="text-gradient bg-gradient-to-r from-heading via-primary-strong to-primary text-[1.35rem] font-semibold tracking-tight">
          The BlackBox Talent
        </p>
      </div>
    </div>
  );
}
